
import {
  computed,
  defineComponent, onBeforeMount, reactive,
} from 'vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import Icone from '@/core/components/Icone.vue';
import Card from '@/core/components/Tela/Card.vue';
import PreviewPdf from '@/core/components/Modal/PreviewPdf.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import { EStatusRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ECustomRenderRow } from '@/core/models/AntDesign/IColumn';
import { useGradeBase } from '@/core/composables/GradeBase';
import { ETipoArquivo } from '@/core/models/Enumeradores/ETipoArquivo';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import BuscaGeral from '@/core/components/BuscaAvancada/BuscaGeral.vue';
import { IArquivoPdf } from '@/core/models/IArquivoPdf';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import ServicoRastreabilidade from '@/servicos/Estoques/ServicoRastreabilidade';
import { IDTORastreabilidade } from '@/models/DTO/Estoques/Rastreabilidade/IDTORastreabilidade';
import DetalheRastreabilidadeModal from '@/components/Cadastros/Produtos/ConsultaProdutos/DetalheRastreabilidadeModal.vue';
import { IPagination } from '@/core/models/AntDesign/IPagination';
import { IListaPaginadaMetaData } from '@/core/models/Consulta/IListaPaginada';
import { IControleProducao } from '@/models/Entidades/Cadastros/PCP/IControleProducao';
import ServicoControleProducao from '@/servicos/Cadastros/PCP/ServicoControleProducao';
import { ETipoControleProducao } from '@/models/Enumeradores/Cadastros/PCP/ETipoControleProcucao';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';

export default defineComponent({
  name: 'DisponibilidadeProdutos',
  components: {
    Icone,
    Card,
    MensagemSemDados,
    BuscaGeral,
    PreviewPdf,
    DetalheRastreabilidadeModal,
    CampoNumerico,
  },
  props: {
    empresa: {
      type: Number,
      required: true,
    },
    modoAjuste: {
      type: Boolean,
      default: false,
    },
    controles: {
      type: Array as () => IControleProducao[],
      default: [] as IControleProducao[],
    },
    produtos: {
      type: Array as () => IDTORastreabilidade[],
      default: () => [],
    },
  },
  emits: ['update:controles', 'update:produtos'],
  setup(props, { emit }) {
    const { telaBase, apresentarMensagemAlerta, apresentarMensagemErro } = useTelaBase();
    const { gradeBase, preparaParametrosConsulta } = useGradeBase();
    const servicoRastreabilidade = new ServicoRastreabilidade();
    const servicoControleProducao = new ServicoControleProducao();
    servicoRastreabilidade.requisicaoSistema();
    servicoControleProducao.requisicaoSistema();

    const state = reactive({
      paginacao: {} as IPagination,
      paginacaoApi: { totalRegistros: 0, paginaAtual: 1 } as IListaPaginadaMetaData,
      produtos: [] as IDTORastreabilidade[],
      arquivosPdf: [] as IArquivoPdf[],
      rastreabilidadeSelecionada: {} as IDTORastreabilidade,
      buscaRapida: '',
      exibirDetalhe: false,
      apenasComSaldo: true,
      apenasRastreabilidade: false,
      indexSelecionado: -1,
      qtdAjustada: 0,
    });

    const computedControles = computed({
      get: () => props.controles,
      set: (val: IControleProducao[]) => {
        emit('update:controles', val);
      },
    });

    const computedProdutos = computed({
      get: () => props.produtos,
      set: (val: IDTORastreabilidade[]) => {
        emit('update:produtos', val);
      },
    });

    function preencherColunas() {
      gradeBase.colunas = [];
      gradeBase.colunas.push({
        title: 'Codigo', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
      });
      gradeBase.colunas.push({
        title: 'Código Prod.', dataIndex: 'codigoProduto', key: 'CodigoProduto', position: 1, visible: true, ordering: true, align: 'left', width: 130,
      });
      gradeBase.colunas.push({
        title: 'Produto', dataIndex: 'descricaoProduto', key: 'DescricaoProduto', position: 2, visible: true, ordering: true, align: 'left', width: 280,
      });
      gradeBase.colunas.push({
        title: 'UN', dataIndex: 'unidade', key: 'Unidade', position: 3, visible: true, ordering: true, align: 'center', width: 60,
      });
      gradeBase.colunas.push({
        title: 'Estoque', dataIndex: 'descricaoEstoque', key: 'DescricaoEstoque', position: 4, visible: true, ordering: true, align: 'left', width: 240,
      });
      gradeBase.colunas.push({
        title: 'Identificador', dataIndex: 'identificador', key: 'Identificador', position: 5, visible: true, ordering: true, align: 'center', width: 120, customRenderRow: ECustomRenderRow.LinkAcao,
      });
      gradeBase.colunas.push({
        title: 'Lote', dataIndex: 'numeroLote', key: 'NumeroLote', position: 6, visible: true, ordering: true, align: 'center', width: 130,
      });
      gradeBase.colunas.push({
        title: 'Fabricação', dataIndex: 'dataFabricacao', key: 'DataFabricacao', position: 6, visible: true, ordering: true, align: 'center', width: 100, customRenderRow: ECustomRenderRow.MascararData,
      });
      gradeBase.colunas.push({
        title: 'Validade', dataIndex: 'dataValidade', key: 'DataValidade', position: 7, visible: true, ordering: true, align: 'center', width: 100, customRenderRow: ECustomRenderRow.MascararData,
      });
      if (computedControles.value.some((c) => c.tipoControle === ETipoControleProducao.Densidade)) {
        gradeBase.colunas.push({
          title: 'Densidade', dataIndex: 'densidade', key: 'Densidade', position: 8, visible: true, ordering: true, align: 'center', width: 90,
        });
      }
      if (computedControles.value.some((c) => c.tipoControle === ETipoControleProducao.PH)) {
        gradeBase.colunas.push({
          title: 'PH', dataIndex: 'ph', key: 'Ph', position: 8, visible: true, ordering: true, align: 'center', width: 80,
        });
      }
      gradeBase.colunas.push({
        title: 'Saldo', dataIndex: 'quantidade', key: 'Quantidade', position: 8, visible: true, ordering: true, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado, width: 100,
      });
      if (props.modoAjuste) {
        gradeBase.colunas.push({
          title: 'Ajuste', dataIndex: 'qtdAjuste', key: 'QtdAjuste', position: 9, visible: true, ordering: true, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado, width: 100,
        });
        gradeBase.colunas.push({
          title: 'Qtd Ajustada', dataIndex: 'qtdAjustada', key: 'QtdAjustada', position: 10, visible: true, ordering: true, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado, width: 100,
        });
      }
      gradeBase.colunas.push({
        title: 'Ações', key: 'acoes', position: 11, visible: true, fixed: 'right', width: 120, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
      });
    }

    function preencherPaginacao() {
      state.paginacao.total = 100;
      state.paginacao.showSizeChanger = true;
      state.paginacao.pageSize = 10;
      state.paginacao.pageSizeOptions = ['5', '10', '50', '100', '500', '1000'];
    }

    function limparSelecaoAjuste() {
      state.indexSelecionado = -1;
      state.qtdAjustada = 0;
    }

    async function obterControles() {
      if (UtilitarioGeral.validaLista(props.controles)) {
        computedControles.value = await servicoControleProducao.obterAtivos(props.empresa);
      }
    }

    async function buscarDados() {
      state.produtos = [];
      state.paginacaoApi.paginaAtual = 1;
      const parametrosConsulta = preparaParametrosConsulta([props.empresa], gradeBase.paginacao.current, gradeBase.paginacao.pageSize, gradeBase.paginacao.total);
      parametrosConsulta.qtdeRegistrosPagina = state.paginacao.pageSize;
      parametrosConsulta.numeroPagina = state.paginacao.current;
      parametrosConsulta.valorBuscaRapida = state.buscaRapida;
      parametrosConsulta.qtdeRegistrosTotal = 0;
      telaBase.carregando = true;

      const listaPaginada = await servicoRastreabilidade.buscaAvancadaRastreabilidade(parametrosConsulta, gradeBase.filtrosAplicados, !state.apenasComSaldo, state.apenasRastreabilidade);
      state.produtos = listaPaginada.dados;
      state.produtos.forEach((p, index) => {
        const indexP = computedProdutos.value.findIndex((prod) => prod.codigo === p.codigo);
        if (indexP !== -1) {
          state.produtos[index].qtdAjustada = computedProdutos.value[indexP].qtdAjustada;
          state.produtos[index].qtdAjuste = computedProdutos.value[indexP].qtdAjuste;
        }
      });
      state.paginacaoApi = listaPaginada.metaData;
      telaBase.carregando = false;
    }

    async function aplicarFiltros() {
      gradeBase.paginacao.current = 1;
      await buscarDados();
    }

    async function imprimir(tipoArquivoRelatorio: ETipoArquivo, codigoRegistro?: number) {
      gradeBase.filtrosAplicados = [];
      const parametrosConsulta = preparaParametrosConsulta([props.empresa], 1, 0, 0);
      let codigosSelecionados: number[] = [];
      if (codigoRegistro !== undefined && codigoRegistro > 0) {
        codigosSelecionados.push(codigoRegistro);
      } else {
        codigosSelecionados = gradeBase.codigosSelecionados;
      }

      telaBase.carregando = true;
      const retornoRelatorio = await servicoRastreabilidade.relatorioPadrao(tipoArquivoRelatorio, parametrosConsulta, gradeBase.filtrosAplicados);
      telaBase.carregando = false;
      if (retornoRelatorio.status === EStatusRetornoRequisicao.Sucesso) {
        if (tipoArquivoRelatorio === ETipoArquivo.PDF) {
          state.arquivosPdf = [{ nome: '', link: retornoRelatorio.link } as IArquivoPdf];
        } else {
          UtilitarioGeral.downloadArquivo(retornoRelatorio.link);
        }
      } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Alerta) {
        apresentarMensagemAlerta(retornoRelatorio.mensagem);
      } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Erro) {
        apresentarMensagemErro(retornoRelatorio.mensagem);
      }
    }

    async function imprimirSelecionado(index: number) {
      telaBase.carregando = true;
      const retornoRelatorio = await servicoRastreabilidade.relatorioMovimentacoes(state.produtos[index].codigo, props.empresa, ETipoArquivo.PDF);
      telaBase.carregando = false;
      if (retornoRelatorio.status === EStatusRetornoRequisicao.Sucesso) {
        state.arquivosPdf = [{ nome: '', link: retornoRelatorio.link } as IArquivoPdf];
      } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Alerta) {
        apresentarMensagemAlerta(retornoRelatorio.mensagem);
      } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Erro) {
        apresentarMensagemErro(retornoRelatorio.mensagem);
      }
    }

    function visualizarSelecionado(index: number) {
      state.rastreabilidadeSelecionada = state.produtos[index];
      state.exibirDetalhe = true;
    }

    function alterarQtdProduto(index: number) {
      state.indexSelecionado = index;
      state.qtdAjustada = state.produtos[index].qtdAjustada ?? 0;
    }

    function confirmarAlteracao() {
      state.produtos[state.indexSelecionado].qtdAjustada = state.qtdAjustada;
      state.produtos[state.indexSelecionado].qtdAjuste = state.qtdAjustada - state.produtos[state.indexSelecionado].quantidade;
      const index = computedProdutos.value.findIndex((p) => p.codigo === state.produtos[state.indexSelecionado].codigo);
      if (index !== -1) {
        computedProdutos.value[index] = state.produtos[state.indexSelecionado];
      } else {
        computedProdutos.value.push(state.produtos[state.indexSelecionado]);
      }
      limparSelecaoAjuste();
    }

    function cancelarAlteracao() {
      limparSelecaoAjuste();
    }

    async function onPageChange(current: number, pageSize: number) {
      state.paginacao.current = current;
      state.paginacao.pageSize = pageSize;
      await buscarDados();
    }

    async function onShowSizeChange(current: number, pageSize: number) {
      state.paginacao.current = 1;
      state.paginacao.pageSize = pageSize;
      await buscarDados();
    }

    onBeforeMount(async () => {
      limparSelecaoAjuste();
      await obterControles();
      preencherColunas();
      preencherPaginacao();
    });

    return {
      props,
      state,
      telaBase,
      gradeBase,
      ECustomRenderRow,
      ETipoArquivo,
      ETipoControleProducao,
      UtilitarioGeral,
      UtilitarioMascara,
      UtilitarioData,
      computedControles,
      computedProdutos,
      buscarDados,
      aplicarFiltros,
      imprimir,
      imprimirSelecionado,
      visualizarSelecionado,
      alterarQtdProduto,
      confirmarAlteracao,
      cancelarAlteracao,
      onPageChange,
      onShowSizeChange,
    };
  },
});
